export interface TranslationIndex {
  exact: Map<string, string[]>;
  normalized: Map<string, string[]>;
}

export interface FindTranslationKeyOptions {
  keyPrefix?: string;
}

const translationIndexCache = new WeakMap<object, TranslationIndex>();

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/**
 * Normalizes an English locale value for matching backend display strings.
 * Locale values are English, so case and whitespace are not meaningful here.
 */
export function normalizeTranslationValue(value: string): string {
  return value.trim().replace(/\s+/g, " ").toLowerCase();
}

function addCandidate(
  values: Map<string, string[]>,
  value: string,
  key: string,
) {
  const candidates = values.get(value);
  if (candidates) {
    candidates.push(key);
  } else {
    values.set(value, [key]);
  }
}

function addValue(index: TranslationIndex, value: string, key: string) {
  addCandidate(index.exact, value, key);
  addCandidate(index.normalized, normalizeTranslationValue(value), key);
}

function visitResource(
  resource: Record<string, unknown>,
  parentKey: string,
  index: TranslationIndex,
) {
  for (const [childKey, childValue] of Object.entries(resource)) {
    const key = parentKey ? `${parentKey}.${childKey}` : childKey;

    if (isRecord(childValue)) {
      visitResource(childValue, key, index);
    } else if (typeof childValue === "string" && childValue.trim()) {
      addValue(index, childValue, key);
    }
  }
}

export function buildTranslationIndex(resource: unknown): TranslationIndex {
  const index: TranslationIndex = {
    exact: new Map(),
    normalized: new Map(),
  };

  if (isRecord(resource)) {
    visitResource(resource, "", index);
  }

  return index;
}

export function getTranslationIndex(resource: unknown): TranslationIndex {
  if (!isRecord(resource)) {
    return buildTranslationIndex(resource);
  }

  const cachedIndex = translationIndexCache.get(resource);
  if (cachedIndex) {
    return cachedIndex;
  }

  const index = buildTranslationIndex(resource);
  translationIndexCache.set(resource, index);
  return index;
}

function matchesKeyPrefix(key: string, keyPrefix?: string): boolean {
  if (!keyPrefix) {
    return true;
  }

  return (
    key === keyPrefix ||
    key.startsWith(`${keyPrefix}.`) ||
    key.startsWith(`${keyPrefix}_`) ||
    key.startsWith(`${keyPrefix}__`)
  );
}

/**
 * Finds the locale key for a backend English display value.
 *
 * Duplicate English values are deliberately kept in insertion order. Callers
 * that know the domain can pass a keyPrefix to disambiguate them.
 */
export function findTranslationKey(
  value: string,
  index: TranslationIndex,
  options: FindTranslationKeyOptions = {},
): string | undefined {
  const candidates =
    index.exact.get(value) ??
    index.exact.get(value.trim()) ??
    index.normalized.get(normalizeTranslationValue(value));

  return candidates?.find((key) => matchesKeyPrefix(key, options.keyPrefix));
}
